import CheckoutSettings from 'apps/commerce/common/checkout/types/CheckoutSettings';
import { CustomerInformation, SubmitCartPayload } from '../types';
import { createPaymentMethod, NoPaymentIntegrationError } from './cartClient';

type PaymentMethods = 'kajabi_payments' | 'stripe' | null;

export const buildOrderPayload = async (getValues: Function): Promise<SubmitCartPayload> => {
  const {
    address,
    checkoutSettings,
    saveCard,
    elements,
    email,
    member,
    name,
    optin,
    updatedAt,
    paymentProvider,
    phone,
    requirePayment = true,
    stripe,
    serviceAgreementChecked,
    useSavedCard,
    taxId,
  } = getValues();
  const {
    nameRequired,
    phoneNumberRequired,
    addressRequired,
    serviceAgreementRequired,
    collectTaxId,
    savedCardDetails,
    showOptIn,
  } = checkoutSettings as CheckoutSettings;

  const memberAttributes = {
    email: email || member.email,
    ...(nameRequired && { name }),
  };

  const customerInformationRequired = phoneNumberRequired || addressRequired || collectTaxId;
  const customerInformation = {
    phone_number: phone,
    address_line_1: address.addressLine1,
    address_line_2: address.addressLine2,
    address_city: address.city,
    address_zip: address.zip,
    address_country: address.country,
    ...(address.subdivisionRequired && { address_state: address.subdivision }),
    ...(taxId && { business_number: taxId }),
  } as CustomerInformation;

  const checkoutOptionsRequired = serviceAgreementRequired || showOptIn;
  const checkoutOptions = {
    ...(serviceAgreementChecked && { serviceAgreement: serviceAgreementChecked }),
    ...(showOptIn && { email_opt_in: optin }),
  };

  const basePayload = {
    originUrl: window.location.href,
    cartUpdatedAt: updatedAt,
    memberAttributes,
    ...(checkoutOptionsRequired && { checkoutOptions }),
    ...(customerInformationRequired && { customerInformation }),
  };

  // We'll eventually refactor this to support Paypal, but for now we can explicitly handle stripe
  let paymentMethod = null;

  if (!requirePayment) return basePayload;

  if (['paypal', 'external_paypal'].includes(paymentProvider)) {
    const paypalDetails = getValues('paypalDetails');
    return {
      ...basePayload,
      paymentDetails: {
        consentToStorePaymentMethod: false,
        paymentProvider: 'paypal' as const,
        paypalDetails,
      },
    };
  }

  // Payment provider is Kajabi Payments or Stripe
  if (useSavedCard && savedCardDetails) {
    paymentMethod = {
      id: savedCardDetails.paymentMethodId,
      type: 'card',
    };
  } else if (stripe && elements) {
    paymentMethod = await createPaymentMethod(elements, email, stripe);
  }

  let paymentIntegration: PaymentMethods = null;
  if (checkoutSettings.paymentMethods.includes('stripe')) {
    paymentIntegration = 'stripe';
  } else if (checkoutSettings.paymentMethods.includes('kajabi_payments')) {
    paymentIntegration = 'kajabi_payments';
  } else {
    throw new NoPaymentIntegrationError('No valid payment integration found');
  }

  const consentToStorePaymentMethod = (saveCard && paymentProvider === 'card') || false;
  return {
    ...basePayload,
    paymentDetails: {
      consentToStorePaymentMethod,
      paymentProvider: paymentIntegration,
      stripeDetails: {
        paymentMethodId: paymentMethod.id,
        paymentMethodType: paymentMethod.type,
      },
    },
  };
};

export default buildOrderPayload;
